import { useState, useEffect, useRef } from "react";
import "./App.css";

import { CURRICULUM } from "./data/curriculum";
import { useAIChat } from "./hooks/useAIChat";
import StarBackground from "./components/StarBackground";
import CelebrationParticles from "./components/CelebrationParticles";
import HomeScreen from "./components/screens/HomeScreen";
import TopicsScreen from "./components/screens/TopicsScreen";
import PracticeScreen from "./components/screens/PracticeScreen";
import ResultScreen from "./components/screens/ResultScreen";
import ChatDrawer from "./components/chat/ChatDrawer";

export default function MathTutor() {
  const [screen, setScreen] = useState("home");
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState({});
  const [showHint, setShowHint] = useState(false);
  const [streak, setStreak] = useState(0);
  const [xp, setXp] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const inputRef = useRef(null);

  const gradeInfo = selectedGrade ? CURRICULUM[selectedGrade] : null;
  const problems = selectedTopic?.problems || [];
  const currentProblem = problems[currentProblemIndex];

  const {
    chatMessages,
    setChatMessages,
    chatInput,
    setChatInput,
    isChatOpen,
    setIsChatOpen,
    isAILoading,
    askAI,
    chatEndRef,
  } = useAIChat({ gradeInfo, topic: selectedTopic, currentProblem });

  useEffect(() => {
    if (screen === "practice" && inputRef.current) inputRef.current.focus();
  }, [screen, currentProblemIndex]);

  const checkAnswer = () => {
    if (!answer.trim()) return;
    const correct =
      answer.trim().toLowerCase() === currentProblem.a.toLowerCase() ||
      answer.trim().replace(/\s/g, "") === currentProblem.a.replace(/\s/g, "");

    if (correct) {
      setFeedback("correct");
      setStreak(s => s + 1);
      const bonus = streak >= 2 ? 20 : 10;
      setXp(x => x + bonus);
      setScore(s => s + 1);
      if (streak >= 2) setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2000);
    } else {
      setFeedback("wrong");
      setStreak(0);
      setAttempts(a => ({ ...a, [currentProblemIndex]: (a[currentProblemIndex] || 0) + 1 }));
    }
  };

  const nextProblem = () => {
    setFeedback(null);
    setAnswer("");
    setShowHint(false);
    if (currentProblemIndex + 1 >= problems.length) {
      setScreen("result");
    } else {
      setCurrentProblemIndex(i => i + 1);
    }
  };

  const startTopic = (topic) => {
    setSelectedTopic(topic);
    setCurrentProblemIndex(0);
    setScore(0);
    setFeedback(null);
    setAnswer("");
    setShowHint(false);
    setAttempts({});
    setChatMessages([{
      role: "assistant",
      text: `Hi! I'm your AI Math Tutor 🤖✨ We're working on **${topic.name}** today. Ask me anything if you get stuck!`
    }]);
    setScreen("practice");
  };

  const resetToHome = () => {
    setScreen("home");
    setSelectedGrade(null);
    setSelectedTopic(null);
    setScore(0);
    setStreak(0);
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0d0d2b 0%, #1a1a4e 40%, #0d1f4e 100%)",
      fontFamily: "'Nunito', 'Segoe UI', sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>
      <StarBackground />
      <CelebrationParticles show={showCelebration} />

      {screen === "home" && (
        <HomeScreen
          xp={xp}
          onSelectGrade={(grade) => { setSelectedGrade(grade); setScreen("topics"); }}
        />
      )}

      {screen === "topics" && gradeInfo && (
        <TopicsScreen
          gradeInfo={gradeInfo}
          onBack={() => setScreen("home")}
          onSelectTopic={startTopic}
        />
      )}

      {screen === "practice" && currentProblem && (
        <PracticeScreen
          topic={selectedTopic}
          gradeInfo={gradeInfo}
          currentProblem={currentProblem}
          currentProblemIndex={currentProblemIndex}
          problems={problems}
          answer={answer}
          setAnswer={setAnswer}
          feedback={feedback}
          score={score}
          attempts={attempts}
          showHint={showHint}
          setShowHint={setShowHint}
          streak={streak}
          xp={xp}
          onCheckAnswer={checkAnswer}
          onNextProblem={nextProblem}
          onBack={() => setScreen("topics")}
          inputRef={inputRef}
          chatHandlers={{ isChatOpen, setIsChatOpen }}
        />
      )}

      {screen === "result" && (
        <ResultScreen
          topic={selectedTopic}
          gradeInfo={gradeInfo}
          score={score}
          problems={problems}
          attempts={attempts}
          xp={xp}
          onTryAgain={() => startTopic(selectedTopic)}
          onMoreTopics={() => setScreen("topics")}
          onHome={resetToHome}
        />
      )}

      <ChatDrawer
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        messages={chatMessages}
        input={chatInput}
        onInputChange={setChatInput}
        onSend={askAI}
        isLoading={isAILoading}
        chatEndRef={chatEndRef}
      />

      {screen === "practice" && !isChatOpen && (
        <button
          onClick={() => setIsChatOpen(true)}
          style={{
            position: "fixed",
            bottom: "24px",
            right: "24px",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            background: "linear-gradient(135deg, #6C63FF, #a09bff)",
            border: "none",
            cursor: "pointer",
            fontSize: "28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 32px rgba(108, 99, 255, 0.5)",
            zIndex: 40,
            animation: "pulse 2s ease-in-out infinite",
          }}>
          🤖
        </button>
      )}
    </div>
  );
}
